/**
 * The interpolator catalogue.
 *
 * The pump reads at a fractional, signed position, so something has to decide
 * what the signal is between two samples. Which kernel is worth its cost depends
 * on the material and on how far the sample rate is being stretched — a 48 kHz
 * asset at 1x doesn't interpolate at all (the fractional part is always zero),
 * while an 8 kHz asset in a 48 kHz context is a 6x upsample and every choice is
 * audible. So it's a runtime setting, not a compile-time one.
 *
 * Measured SNR, 24 kHz asset in a 48 kHz context with speech-band content:
 *
 *   linear      26.5 dB      2.7 ns/sample
 *   hermite     42.0 dB     12.7
 *   farrow-6    56.1 dB     75.0
 *   farrow-8    69.5 dB    112.8
 *   sinc-8      75.9 dB     16.8
 *   sinc-16    ~99   dB     30.2
 *
 * For scale: the audio thread gets a 128-sample quantum every 2.67 ms, so even
 * the dearest of these is under 1% of budget across four tracks. Cost is not the
 * deciding factor here; quality is.
 */

import { LINEAR, readLinear } from "./linear.js";
import { HERMITE, readHermite } from "./hermite.js";
import { createFarrow, readFarrow } from "./farrow.js";
import { createWindowedSinc, readWindowedSinc } from "./windowedSinc.js";
import { createHybrid, readHybrid } from "./hybrid.js";

/**
 * Kernel implementations by family, injected into the worklet.
 *
 * They are serialised with `Function.prototype.toString()`, so each one must be
 * **self-contained** — no closure variables, no imports, no references to
 * anything outside its own arguments. Everything variable arrives through
 * `table` and `params`.
 */
export const INTERPOLATOR_KERNELS = {
  linear: readLinear,
  hermite: readHermite,
  farrow: readFarrow,
  sinc: readWindowedSinc,
  hybrid: readHybrid,
};

/** Ordered worst-to-best, which is also the order a menu should show them in. */
export const INTERPOLATORS = [
  LINEAR,
  HERMITE,
  createFarrow(6),
  createFarrow(8),
  createWindowedSinc(8),
  createWindowedSinc(16),
  createWindowedSinc(32, {
    notes: "Diminishing returns above this; kept for A/B reference.",
  }),
  createHybrid(),
];

/**
 * Sixteen-tap sinc.
 *
 * The deciding case is 8 kHz speech, where the content occupies most of the
 * band — measured with partials out to 3 kHz (0.75x Nyquist):
 *
 *   linear 12.8 · hermite 16.5 · farrow-8 21.6 · sinc-8 21.4 · sinc-16 43.8 · sinc-32 55.4 dB
 *
 * Everything short clusters together down there, because a short kernel's
 * transition band reaches into the signal. Only length fixes that, and 16 taps
 * is where it starts paying: +22 dB over sinc-8 for 30 ns a sample. Well below
 * Nyquist the order flattens out and sinc-8 would do, but af's 8 kHz recordings
 * are the near-Nyquist case, so the default should be the one that handles them.
 */
export const DEFAULT_INTERPOLATOR_ID = "sinc-16";

export function getInterpolator(id) {
  return INTERPOLATORS.find((interpolator) => interpolator.id === id) || null;
}

/** Serialisable description of each option, for building a menu. */
export function interpolatorCatalogue() {
  return INTERPOLATORS.map((interpolator) => ({
    id: interpolator.id,
    label: interpolator.label,
    taps: interpolator.taps,
    notes: interpolator.notes,
    isDefault: interpolator.id === DEFAULT_INTERPOLATOR_ID,
  }));
}

/**
 * Everything the pump needs to run a kernel, ready to post across.
 *
 * Some kernels depend on the rate ratio — the hybrid's anti-imaging filter is
 * designed from it, exactly as the MATLAB derives `intFactor` from srIn and
 * srOut — so `rates` must be supplied when one of those is selected.
 *
 * @param {string} id
 * @param {Object} [rates] - {fileRate, contextRate}
 */
export function interpolatorConfig(id, rates = null) {
  const interpolator = getInterpolator(id);
  if (!interpolator) {
    const available = INTERPOLATORS.map((i) => i.id).join(", ");
    throw new Error(`Unknown interpolator "${id}". Available: ${available}`);
  }

  if (interpolator.needsRates && (!rates || !rates.fileRate || !rates.contextRate)) {
    throw new Error(
      `Interpolator "${id}" is designed from the rate ratio and needs ` +
        `{fileRate, contextRate}.`
    );
  }

  const resolved = interpolator.configure
    ? interpolator.configure(rates)
    : {
        table: interpolator.buildTable(),
        params: interpolator.params,
        before: interpolator.before,
        after: interpolator.after,
        taps: interpolator.taps,
      };

  return {
    id: interpolator.id,
    kernel: interpolator.kernel,
    // Interpolators return the sample untouched when the position lands exactly
    // on one, so the pump can skip them. A kernel that *filters* — the hybrid's
    // anti-imaging FIR — must run regardless, or it would emit raw samples at
    // every exact hit, which at 8 kHz into 48 kHz is one output in six.
    identityAtZero: interpolator.identityAtZero !== false,
    ...resolved,
  };
}
